/*
 * *****************************************************************************
 *  Copyright (C)  Motorola Solutions, INC.
 *  All Rights Reserved.
 *  Motorola Solutions Confidential Restricted.
 *  ******************************************************************************
 */

import {
    Call,
    CallStatus,
    DialableEntity,
    DialedContact,
    LinkedCall,
    LocalParticipant,
    QueueJoin,
    RemoteParticipant,
    UserAuthenticationRecord
} from 'CalltakingCoreApi';
import { SortFunctions } from './sort-functions';
import { callFilter } from '../../settings/model/settings.model';
import { Bid } from '../../user/model/bid';
import { BidResult } from '../../user/model/bid-result';
import { Dictionary } from '@ngrx/entity';
import { HotKeyAction } from '../../core/model/hotkey';
import dayjs from 'dayjs';
import { LinkedCallData } from '../model/linked-call-data';
import { displayToastNotification } from '../../notification/+state/notification.actions';
import { ToastType } from '@msi/cobalt';
import { CentralPaneSettings } from '../../configuration/model/layout-settings';

export class CallFunctions {
    public static readonly liveStatuses: CallStatus[] = ['CONNECTED', 'RINGING', 'ON_HOLD', 'TRANSFERRED'];

    public static readonly historicalStatuses: CallStatus[] = ['RELEASED', 'ABANDONED_REDIAL', 'ABANDONED_CLEARED'];

    private static readonly abandonedStatuses: CallStatus[] = ['ABANDONED', 'ABANDONED_INVALID_REDIAL'];

    private static readonly abandonedActiveStatuses: CallStatus[] = ['ABANDONED', 'ABANDONED_REDIALING'];

    private static readonly abandonedServicedStatuses: CallStatus[] = ['ABANDONED_REDIAL', 'ABANDONED_CLEARED'];

    private static readonly activeCallStatuses: CallStatus[] = ['CONNECTED', 'ON_HOLD'];

    private static readonly callHotkeyOperations: HotKeyAction[] = [
        HotKeyAction.ANSWER,
        HotKeyAction.DIAL,
        HotKeyAction.DIAL_CONTACT,
        HotKeyAction.CONFERENCE_RELEASE,
        HotKeyAction.RELEASE,
        HotKeyAction.CALLBACK_SELECTED,
        HotKeyAction.DEAFEN,
        HotKeyAction.HOLD,
        HotKeyAction.MUTE,
        HotKeyAction.VOLUME_DOWN,
        HotKeyAction.VOLUME_UP
    ];

    public static readonly VESTA_ROUTER_CALLER = '911-caller';
    public static readonly VESTA_ROUTER_CALLEE = '911-callee';
    public static readonly VESTA_ROUTER_REFERRED = '911-referred';

    public static isRinging(call: Call, username?: string): boolean {
        return username && call.participants[username] ? call.participants[username].status === 'RINGING' : call.status === 'RINGING';
    }

    public static isActive(callStatus: CallStatus) {
        return this.activeCallStatuses.includes(callStatus);
    }

    public static isConnected(status: CallStatus): boolean {
        return status === 'CONNECTED';
    }

    public static isHeld(call: Call, username?: string): boolean {
        return CallFunctions.isParticipant(call, username) ? CallFunctions.isHeldParticipant(call, username)
            : call.status === 'ON_HOLD';
    }

    public static isHoldable(call: Call, layoutCentralPane: CentralPaneSettings): boolean {
        return (!call.text)
            ? (layoutCentralPane.callDetails.callHeader.hold &&
            (CallFunctions.getActiveRemoteParticipantCount(call) >= 1) || (CallFunctions.getActiveLocalParticipantCount(call) > 2))
            : false;
    }

    public static isMuted(call: Call, username?: string): boolean {
        return username && call.participants[username] ? call.participants[username].muted : false;
    }

    public static isReleased(status: CallStatus): boolean {
        return status === 'RELEASED' || status === 'TRANSFERRED' || this.isAbandoned(status);
    }

    public static isLive(status: CallStatus): boolean {
        return this.liveStatuses.includes(status);
    }

    public static isAbandoned(status: CallStatus): boolean {
        return this.abandonedStatuses.includes(status);
    }

    public static inProcessAac(call: Call): boolean {
        return this.isAbandoned(call.status) && call.aacFiltered;
    }

    public static isAbandonedDuplicated(call: Call): boolean {
        return this.abandonedActiveStatuses.includes(call.status) && !(call.abandonedUniquenessConfirmed ?? true);
    }

    public static isAbandonedServiced(status: CallStatus): boolean {
        return this.abandonedServicedStatuses.includes(status);
    }

    public static isHistorical(status: CallStatus): boolean {
        return this.historicalStatuses.includes(status);
    }

    public static isParticipant(call: Call, username: string | undefined): boolean {
        return Boolean(username && call.participants && call.participants[username]);
    }

    public static isHeldParticipant(call: Call, username: string | undefined): boolean {
        return this.isParticipant(call, username) && call.participants[username].held && !call.participants[username].silentMonitor;
    }

    public static isActiveParticipant(call: Call, username: string | undefined): boolean {
        return call && username !== undefined && this.isParticipant(call, username) && !call.participants[username].leftOn;
    }

    public static isInactiveParticipant(call: Call, username: string | undefined): boolean {
        return username !== undefined && this.isParticipant(call, username) && Boolean(call.participants[username].leftOn);
    }

    public static hasActiveParticipants(call: Call): boolean {
        return Object.keys(call.participants)?.length > 0;
    }

    public static getActiveLocalParticipantCount(call: Call): number {
        return Object.values(call.participants).filter((participant: LocalParticipant) => !participant.leftOn && !participant.silentMonitor).length;
    }

    public static getActiveRemoteParticipantCount(call: Call): number {
        return call.remoteParticipants.filter((participant: RemoteParticipant) => !participant.leftOn).length;
    }

    public static getActiveParticipantCount(call: Call): number {
        return CallFunctions.getActiveLocalParticipantCount(call) + CallFunctions.getActiveRemoteParticipantCount(call);
    }

    public static getFirstCallTaker(call: Call): string | undefined {
        let participants = Object.values(call.participants);
        return participants?.sort(SortFunctions.joinedOnSort).find((participant) => !participant.leftOn)?.name;
    }

    public static isUserACDAssignedAgent(call: Call, username: string): boolean {
        return Boolean(call.acdAssignedAgent && call.acdAssignedAgent.toUpperCase() === username.toUpperCase());
    }

    public static isUserAssignedOrInvolved(call: Call, username: string) {
        return this.isUserACDAssignedAgent(call, username) || this.isParticipant(call, username);
    }

    public static isUserHolding(call: Call, username: string | undefined): boolean {
        return (
            username !== undefined &&
            this.isActiveParticipant(call, username) &&
            // check user participant held or holding
            (call.participants[username].held || call.participants[username].holding || call.participants[username].status === 'ON_HOLD') &&
            // check no active local participants
            // @ts-ignore
            Object.values(call.participants).every((p) => !!p.leftOn) &&
            // check one remote active participant
            // @ts-ignore
            call.remoteParticipants.filter((p) => !p.leftOn).length === 1
        );
    }

    public static hasUserReleased(call: Call, username: string) {
        return CallFunctions.isHistorical(call?.status) ||
            (CallFunctions.isLive(call?.status) && !CallFunctions.isActiveParticipant(call, username));
    }

    public static isDialedByUser(call: Call, username: string | undefined): boolean {
        // Sanity Check
        if (!username || !call || call.name !== username || call.text) {
            return false;
        }
        return (
            call.type === 'INTERNAL' ||
            (call.status === 'RINGING' &&
                // @ts-ignore
                !Object.values(call.participants).filter((p) => !p.leftOn).length)
        );
    }

    public static getCallStatus(call: Call, user: UserAuthenticationRecord | undefined): CallStatus | null {
        let callStatus: CallStatus | null;
        if (!CallFunctions.isAbandoned(call.status) && CallFunctions.ringingMe(call, user)) {
            callStatus = 'RINGING';
        } else if (call && call.externalTransfer) {
            callStatus = 'TRANSFERRED';
        } else {
            callStatus = this.getUserSpecificPropertyFromCall(call, user?.username, 'status') as CallStatus;
        }
        return callStatus;
    }

    public static isCallRingingUser(call: Call, user: UserAuthenticationRecord) {
        const notParticipating = !CallFunctions.isActiveParticipant(call, user?.username) || CallFunctions.isSilentMonitoring(call, user?.username);
        const ringingMe = CallFunctions.ringingMe(call, user);
        return notParticipating && ringingMe;
    }

    public static isRingingPrimaryQueue(call: Call, user: UserAuthenticationRecord) {
        let primaryQueue = CallFunctions.getCurrentQueueJoin(call);
        return Boolean(primaryQueue && user.associatedCallQueues.includes(primaryQueue.queueName));
    }

    public static getOldestTimestamp<T extends { updatedTimestamp: string }>(obj: T[]) {
        if (!obj.length) return 0;
        return obj.reduce((oldest, curr) => {
            return dayjs(curr.updatedTimestamp).isBefore(dayjs(oldest.updatedTimestamp)) ? curr : oldest;
        }).updatedTimestamp;
    }

    public static ringingMe(call: Call, user: UserAuthenticationRecord | undefined, includeUnassignedAcdCallQueues: boolean = true) {
        return (
            call &&
            user &&
            call.status !== 'RELEASED' &&
            (call.ringingUsers.includes(user.username) ||
                CallFunctions.getRingingCallQueues(call, user.username, includeUnassignedAcdCallQueues).some((cq) => user.associatedCallQueues.includes(cq)) ||
                call.acdAssignedAgent === user.username) && !call.abandonedAcknowledgedBy && !CallFunctions.isAbandonedServiced(call.status)
        );
    }

    public static abandonedInUserQueues(call: Call, user: UserAuthenticationRecord | undefined) {
        return (
            call &&
            user &&
            !call.abandonedQueueJoin?.leftOn &&
            user.associatedCallQueues.includes(call.abandonedQueueJoin?.queueName)
        );
    }

    public static getUserSpecificPropertyFromCall(call: Call, username: string | undefined, property: keyof Call | keyof LocalParticipant) {
        if (!call) {
            return null;
        }
        if (username && this.isActiveParticipant(call, username)) {
            let participantProperty = property as keyof LocalParticipant;
            return call.participants[username][participantProperty];
        } else {
            let callProperty = property as keyof Call;
            return call[callProperty];
        }
    }

    // input is list of live calls with username already a participant
    // probably use output list as input for an effect that monitors current active call and the 'priority' list
    // in order to determine if the 'active' call should change
    static prioritySort(username: string | undefined) {
        return (a: Call, b: Call) => {
            return (
                Number(this.isActiveParticipant(b, username)) - Number(this.isActiveParticipant(a, username)) ||
                Number(this.isDialedByUser(b, username)) - Number(this.isDialedByUser(a, username)) ||
                Number(this.isUserHolding(b, username)) - Number(this.isUserHolding(a, username))
            );
        };
    }

    static findMostRecentUserVisibleACDQueueJoin(queueJoins: QueueJoin[] | undefined, userCallQueues: string[] | undefined) {
        return queueJoins && userCallQueues
            ? [...queueJoins]
                  .sort(SortFunctions.oldestCreatedSort)
                  .find((queueJoin) => queueJoin.acd && userCallQueues.includes(queueJoin.queueName) && queueJoin.joinedOn)
            : undefined;
    }

    static isSilentMonitoring(call: Call, username: string | undefined): boolean {
        return Boolean(call && username && this.isActiveParticipant(call, username) && call.participants[username]?.silentMonitor);
    }

    public static getCallTypeIndication(call: Call) {
        const latestQueueJoin = CallFunctions.getLatestQueueJoin(call);
        if (call.agencyDiversion) {
            return 'Diversion';
        } else if (latestQueueJoin?.supervisorAssistance) {
            return 'Assistance';
        } else if (call.aacRedial ) {
            return 'Abandoned Callback';
        }  else if (call.type === 'INTERNAL') {
            return 'Intercom';
        } else if (call.rePresented) {
            return 'Recovery';
        }else if (call.status === 'ABANDONED') {
            return 'Abandoned'
        }
        return undefined;
    }

    public static isAssistanceRequest(call: Call, user: UserAuthenticationRecord) {
        const latestQueueJoin = CallFunctions.getLatestQueueJoin(call);
        return latestQueueJoin?.supervisorAssistance && user?.associatedCallQueues.includes(latestQueueJoin.queueName);
    }

    public static getOriginatingUsername(call: Call) {
        // Internal calls do not have queues
        if (call.type === 'INTERNAL') {
            return Object.values(call.participants).sort(SortFunctions.joinedOnSort)[0]?.name;
        } else {
            const latestQueueJoin = CallFunctions.getLatestQueueJoin(call);
            if (latestQueueJoin) {
                return latestQueueJoin.originatingUsername;
            }
        }
        return undefined;
    }

    public static getLatestQueueJoin(call: Call) {
        return call ? [...CallFunctions.getCallQueueJoins(call)]?.sort(SortFunctions.joinedOnSort).reverse()[0] : undefined;
    }

    public static getCurrentQueueJoin(call: Call) {
        const allQueueJoins = CallFunctions.getCallQueueJoins(call);
        return allQueueJoins.find((queueJoin) => !queueJoin.leftOn);
    }

    public static getRingingCallQueues(call: Call, username: string, includeUnassignedAcdCallQueues: boolean = true) {
        let currentQueue = CallFunctions.getCurrentQueueJoin(call);
        let visible = includeUnassignedAcdCallQueues || (currentQueue && !currentQueue.acd) || (call.acdAssignedAgent === username);
        return currentQueue && visible ? [currentQueue.queueName, ...currentQueue.cascades] : [];
    }

    public static filterCallsByTableFilter(calls: Call[], filters: callFilter[]) {
        return calls.filter((call) => {
            // consider redialing status as abandoned for filtering purposes
            let callStatus = call.status === 'ABANDONED_REDIALING' ? 'ABANDONED' : call.status;
            
            if (callStatus && filters.includes(callStatus.toLowerCase() as callFilter)) {
                return false;
            }

            if (call.type && filters.includes(call.type.toLowerCase() as callFilter)) {
                return false;
            }

            const callType = call?.priority?.emergency ? 'emergency' : 'nonEmergency';

            return !filters.includes(callType);
        });
    }

    public static filterRecentCalls(calls: Call[], filters: callFilter[], lockedCallIds: string[]) {
        return CallFunctions.filterCallsByTableFilter(calls, filters).filter((call) => {
            return filters.includes('locked') ? !lockedCallIds.includes(call.uuid) : filters.includes('unlocked') ? lockedCallIds.includes(call.uuid) : true;
        });
    }

    /*
    * Excludes network participants that have left and 'you' from the network participants listed on the conference snapshot (conference-info xml)
    * */
    public static networkParticipants(call: Call) {
        let linkedCallUri = call.linkedCalls.find((lc) =>
            lc.linkedAgencyId === call.agencyId && lc.linkedCallId === call.uuid)?.linkedConferenceParticipantUri;
        return call.conferenceSnapshot
            ? call.conferenceSnapshot.participants
                  .filter((participant) => !linkedCallUri || linkedCallUri !== participant.uri)
                  .filter((participant) => !participant.leaveTime || call.status === 'RELEASED')
            : [];
    }

    public static mapNetworkParticipantsToLinkedCalls(call: Call, linkedCalls: LinkedCallData[]) {
        return call && call.conferenceSnapshot ? call.conferenceSnapshot.participants.reduce<{ [conferenceParticipantId: string]: LinkedCallData }>((map, conferenceParticipant) =>
            ({ ...map, [conferenceParticipant.id]: linkedCalls.sort((a: LinkedCallData, b: LinkedCallData) => SortFunctions.newestCreatedSort(a.call, b.call))
                    .find((linkedCall) => linkedCall.linkedAgencyId === conferenceParticipant.agencyId && linkedCall.linkedConferenceParticipantUri === conferenceParticipant.uri) }), {}) : {};
    }

    public static dialedContactsFilter() {
        return (c: DialedContact) => !c.dialEnd || (c.sipRefer && c.status === 'ANSWERED' && !c.conferenceParticipantAnswered);
    }

    public static localParticipantFilter(username: string, call: Call) {
        return (lp: LocalParticipant) =>
            (!lp.silentMonitor || lp.name === username) && ((call.status === 'ON_HOLD' && lp.held) || !lp.leftOn || call.status === 'RELEASED');
    }

    public static remoteParticipantFilter(call: Call, linkedCallData: LinkedCallData[], localCallHasConferenceSnapshots: boolean, parentCalls: Call[]) {
        return (rp: RemoteParticipant) => {
            // find the call associated with this remote participant and exclude parent calls to avoid infinite recursion
            let callIdsToExclude = parentCalls.filter((c) => call.uuid !== c.uuid).map((c) => c.uuid);
            let remoteParticipantsLinkedCallId = CallFunctions.findLinkedCallBySourceRemoteParticipant(linkedCallData, call, rp.uuid)?.linkedCallId;
            if (remoteParticipantsLinkedCallId && callIdsToExclude.includes(remoteParticipantsLinkedCallId)) {
                return false;
            }

            let isCaller = call.callerParticipantId === rp.uuid;
            let isNetworkBridgedNxtLink = localCallHasConferenceSnapshots && call && call.linkedCalls?.some((linkedCall) =>
                linkedCall.linkedAgencyId === call.agencyId && linkedCall.linkedCallId === call.uuid && linkedCall.linkedConferenceParticipantUri);
            let isRemoteParticipantConnectedOrCallIsReleased = !rp.leftOn || call.status === 'RELEASED';
            return isRemoteParticipantConnectedOrCallIsReleased && (!isCaller || (isCaller && !isNetworkBridgedNxtLink));
        };
    }

    public static findLinkedCallBySourceRemoteParticipant(linkedCallData: LinkedCallData[], targetCall: Call, remoteParticipantId: string) {
        let linkedCall: LinkedCall;
        let isCaller = targetCall?.callerParticipantId === remoteParticipantId;
        // we want the most recently created call (we may have duplicate/dead links)
        linkedCallData.sort((a: LinkedCallData, b: LinkedCallData) => SortFunctions.newestCreatedSort(a.call, b.call));
        // find direct reference
        linkedCall = linkedCallData.find((linkedCall) => targetCall?.uuid === linkedCall.sourceAgencyCallId && linkedCall.sourceAgencyRemoteParticipantId === remoteParticipantId);
        // fallback to indirect reference for local call's caller
        if (!linkedCall && isCaller) {
            let targetLinkedCall = linkedCallData.find((linkedCall) => targetCall?.uuid === linkedCall.linkedCallId);
            linkedCall = linkedCallData.find((linkedCall) => targetLinkedCall.sourceAgencyId === linkedCall.linkedAgencyId && targetLinkedCall.sourceAgencyCallId === linkedCall.linkedCallId);
        }
        return linkedCall;
    }

    public static remoteParticipantCountFilter(call: Call, linkedCalls: LinkedCallData[]) {
        return (rp: RemoteParticipant) => {
            let isCaller = rp.uuid === call.callerParticipantId;
            let isNetworkBridgedNxtLink = call && call.linkedCalls?.some((linkedCall) =>
                call.uuid === linkedCall.linkedCallId && call.agencyId  === linkedCall.linkedAgencyId && linkedCall.linkedConferenceParticipantUri);
            let isRemoteParticipantConnectedOrCallIsReleased = !rp.leftOn || call.status === 'RELEASED';
            let isDirectlyBridgedNxtLinkRepresentingTheLocalCall = call && call.linkedCalls?.some((linkedCall) =>
                call.uuid === linkedCall.linkedCallId && call.agencyId === linkedCall.linkedAgencyId && linkedCall.sourceAgencyRemoteParticipantId);
            let isSourceRemoteParticipantForBridgedNxtLinkParticipant = call && linkedCalls?.some((linkedCall) => linkedCall.sourceAgencyRemoteParticipantId === rp.uuid);
            return isRemoteParticipantConnectedOrCallIsReleased && !isSourceRemoteParticipantForBridgedNxtLinkParticipant && (!isCaller || (isCaller && !(isNetworkBridgedNxtLink || isDirectlyBridgedNxtLinkRepresentingTheLocalCall)));
        };
    }

    public static networkParticipantCount(call: Call, linkedCalls: LinkedCallData[]) {
        return call?.conferenceSnapshot
            ? call.conferenceSnapshot.participants.filter((cp) => {
                let notMe = cp.agencyId !== call.agencyId;
                let hasLinkedCallSnapshots = linkedCalls.map((lc) => lc.call).some((call) => call.agencyId === cp.agencyId);
                return notMe && !hasLinkedCallSnapshots && (!cp.leaveTime || CallFunctions.isReleased(call.status));
            }).length
            : 0;
    }

    public static participantCount(call: Call, username: string, linkedCalls: LinkedCallData[]) {
        return (
            call.dialedContacts.filter(CallFunctions.dialedContactsFilter()).length +
            Object.values(call.participants).filter(CallFunctions.localParticipantFilter(username, call)).length +
            call.remoteParticipants.filter(CallFunctions.remoteParticipantCountFilter(call, linkedCalls)).length
        );
    }

    public static lastParticipant(call: Call, username: string) {
        let participants: (LocalParticipant | RemoteParticipant)[] = [];
        participants = participants.concat(call.remoteParticipants.filter((participant) => !participant.leftOn));
        participants = participants.concat(Object.values(call.participants).filter((participant) => !participant.leftOn && participant.name !== username));
        participants.sort(SortFunctions.joinedOnSort).reverse();
        return participants[0];
    }

    public static lastPendingParticipant(call: Call) {
        return call.dialedContacts?.filter((c) => !c.dialEnd).sort(SortFunctions.newestCreatedSort)[0];
    }

    public static isVestaNetworkDisplayName(displayName: string) {
        return (
            displayName === CallFunctions.VESTA_ROUTER_CALLER ||
            displayName === CallFunctions.VESTA_ROUTER_CALLEE ||
            displayName === CallFunctions.VESTA_ROUTER_REFERRED
        );
    }

    public static isCallHotkeyOperation(action: HotKeyAction) {
        return this.callHotkeyOperations.includes(action);
    }

    public static activeNonAcdQueueWithAuction(call: Call | undefined) {
        return Boolean(call) && this.getCallQueueJoins(call as Call)
            .find((queueJoin) => !queueJoin.acd && !queueJoin.leftOn && queueJoin.auctionId);
    }

    /* TODO just adding represented check to previously assigned check isn't sufficient for situations where an agency has multiple refer'd instances of a call
        ie: police conferences fire twice, and the second call to fire trips representation, then the first call's agent/client would attempt to bid on and reject
        the represented call with the same nenaId over and over */
    public static createBids(ringingCalls: Call[], bidsMap: Dictionary<BidResult>, previousNenaId: string, incomingCallsFirst: boolean): Bid[] {
        return ringingCalls.sort(incomingCallsFirst ? SortFunctions.acdPriorityIncomingFirstSort : SortFunctions.acdPriorityAbandonedFirstSort).flatMap((call) =>
            CallFunctions.getCallQueueJoins(call)
                .filter((qj) => qj.acd && !qj.leftOn && qj.auctionId && !Boolean(bidsMap[call.uuid] && bidsMap[call.uuid]?.auctionId === qj.auctionId))
                .map(
                    (queueJoin) =>
                        ({
                            auctionId: queueJoin.auctionId,
                            callId: call.uuid,
                            nenaId: call.nenaCallId,
                            clusterName: call.clusterName,
                            queueJoin: queueJoin,
                            previouslyAssigned: Boolean(call.rePresented && call.nenaCallId && call.nenaCallId === previousNenaId)
                        }) as Bid
                )
        );
    }

    public static getCallQueueJoins(call: Call): QueueJoin[] {
        return  call.queueJoins.concat(call.abandonedQueueJoin || [])
    }

    public static hasParticipantWithNumber(call: Call, number: string) {
        if (!call || !number) {
            return false;
        }
        let participants: DialableEntity[] = [...call.remoteParticipants];
        participants.push(...Object.values(call.participants).map((lp) => lp as DialableEntity));
        participants.push(...call.dialedContacts.map((dc) => dc as DialableEntity));
        return participants.some((p) => p.number?.includes(number)) || call.callback?.includes(number);
    }

    public static holdCallValidationErrorAction = () =>
        displayToastNotification({ message: 'Current call must be ended before starting a new one.', level: ToastType.error });
}
